import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowDown, FileText, TrendingUp } from "lucide-react";
import api from "@/lib/api";
import Reveal from "@/components/Reveal";
import StockTicker from "@/components/StockTicker";
import MeshGradient from "@/components/MeshGradient";
import Sparkline from "@/components/Sparkline";
import MarqueeStrip from "@/components/MarqueeStrip";
import Manifesto from "@/components/Manifesto";
import BentoQuickview from "@/components/BentoQuickview";
import CareersBlock from "@/components/CareersBlock";
import { useSettings } from "@/contexts/SettingsContext";
import { resolveAssetUrl } from "@/components/Logo";

const DEFAULT_HERO_IMG = "https://images.pexels.com/photos/358549/pexels-photo-358549.jpeg";

// Deterministic-ish "intraday" sparkline series — looks alive without flicker
function genSeries(seed = 1.0, length = 24) {
  const out = [];
  let v = 20 + seed * 5;
  for (let i = 0; i < length; i++) {
    v += (Math.sin(i * 0.7 + seed) + Math.cos(i * 0.41) * 0.5) * 0.6;
    v += (Math.random() - 0.5) * 0.18;
    out.push(v);
  }
  // ensure last point trends up so percent_change feels positive when applicable
  return out;
}

export default function Home() {
  const s = useSettings();
  const [content, setContent] = useState(null);
  const [featured, setFeatured] = useState([]);
  const [disclosures, setDisclosures] = useState([]);
  const [stock, setStock] = useState(null);

  useEffect(() => {
    api.get("/home").then((r) => setContent(r.data)).catch(() => setContent({}));
    api.get("/news").then((r) => setFeatured(r.data.slice(0, 4))).catch(() => {});
    api.get("/disclosures").then((r) => setDisclosures(r.data.slice(0, 5))).catch(() => {});
    api.get("/stock/abg").then((r) => setStock(r.data)).catch(() => {});
  }, []);

  const positive = (stock?.percent_change ?? 0) >= 0;
  const c = content || {};
  const heroImg = c.hero_image_url ? resolveAssetUrl(c.hero_image_url) : DEFAULT_HERO_IMG;

  // Stable per-mount sparkline (avoids re-flicker)
  const series = useMemo(() => genSeries(1.4, 28), []);

  return (
    <div data-testid="home-page">
      {/* HERO — tech-driven */}
      <section className="relative min-h-[100svh] flex flex-col bg-emerald-ink text-bone overflow-hidden">
        {/* Background image (subtle, behind everything) */}
        <div className="absolute inset-0">
          <img src={heroImg} alt="" className="w-full h-full object-cover opacity-25" loading="eager" fetchPriority="high" decoding="async" />
        </div>
        {/* Mesh gradient */}
        <MeshGradient />
        {/* Dotted grid */}
        <div className="absolute inset-0 grid-bg opacity-60" />
        {/* Overlay */}
        <div className="absolute inset-0 hero-overlay" />
        {/* Grain */}
        <div className="grain" />

        <div className="relative flex-1 flex items-center container-x pt-28 sm:pt-32 pb-16 sm:pb-24">
          <div className="w-full grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
            {/* LEFT: Copy */}
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
              className="lg:col-span-7"
            >
              <h1 className="font-display text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-[5.5rem] leading-[1.02] tracking-tight">
                <span className="gradient-text">{c.hero_title_1 || "Patient capital."}</span><br />
                <span className="gradient-text">
                  {c.hero_title_2_prefix ? <>{c.hero_title_2_prefix} </> : null}
                </span>
                <span className="accent-italic">{c.hero_title_2_emphasis || "Principled"}</span>
                <span className="gradient-text">{" "}{c.hero_title_2_suffix || "operators."}</span><br />
                <span className="gradient-text">{c.hero_title_3 || "Enduring value."}</span>
              </h1>

              <p className="mt-6 sm:mt-8 text-bone/65 text-base sm:text-lg max-w-xl leading-relaxed font-light">
                {c.hero_subtitle || `${s.company_name} is a publicly listed Philippine holding company.`}
              </p>

              <div className="mt-8 sm:mt-10 flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-center gap-3 sm:gap-4">
                <Link to={c.hero_cta_primary_link || "/p/the-asiabest-difference"} data-testid="hero-about-cta" className="btn-primary">
                  {c.hero_cta_primary_label || "Discover Our Story"}
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </motion.div>

            {/* RIGHT: Live data terminal */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 1, delay: 0.3, ease: [0.16, 1, 0.3, 1] }}
              className="lg:col-span-5 hidden lg:block"
              data-testid="hero-stock-card"
            >
              <div className="panel-data corner-ticks relative p-7">
                <div className="flex items-center justify-between mb-1 relative">
                  <div className="flex items-center gap-2.5">
                    <span className="live-dot" />
                    <span className="overline text-neon">LIVE · {s.stock_symbol || "PSE : ABG"}</span>
                  </div>
                  <span className="overline text-bone/40 text-[0.6rem]">PHP</span>
                </div>
                <div className="text-bone/50 text-xs mt-1 font-mono">{s.company_name}</div>

                <div className="mt-6 flex items-end justify-between gap-4">
                  <div>
                    <div className="font-mono font-medium text-bone text-5xl md:text-6xl leading-none tracking-tight">
                      ₱{stock?.price ? Number(stock.price).toFixed(2) : "25.40"}
                    </div>
                    <div
                      className="mt-3 flex items-center gap-2 text-sm font-mono"
                      style={{ color: positive ? "hsl(152 76% 60%)" : "#fca5a5" }}
                    >
                      <TrendingUp className="w-4 h-4" />
                      {positive ? "+" : ""}{(stock?.percent_change ?? 1.24).toFixed(2)}%
                      <span className="text-bone/40">today</span>
                    </div>
                  </div>
                  <div style={{ color: positive ? "hsl(152 76% 55%)" : "#fca5a5" }} className="flex-shrink-0">
                    <Sparkline data={series} width={160} height={64} strokeWidth={1.5} />
                  </div>
                </div>

                {/* Live volume (only genuinely-live secondary stat) */}
                <div className="mt-6 pt-5 border-t border-white/5 flex justify-between text-[0.7rem] font-mono text-bone/50">
                  <span className="overline text-bone/40 text-[0.55rem]">Volume</span>
                  <span className="text-bone/80">
                    {stock?.volume != null ? Number(stock.volume).toLocaleString() : "—"}
                  </span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Scroll indicator */}
        <motion.div
          className="absolute bottom-8 left-1/2 -translate-x-1/2 text-bone/40 flex flex-col items-center gap-2"
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          <span className="overline text-[0.55rem]">Scroll</span>
          <ArrowDown className="w-4 h-4" />
        </motion.div>
      </section>

      <StockTicker />

      {/* INTRO STRIP */}
      <section className="section-y bg-bone relative overflow-hidden">
        <div className="absolute inset-0 grid-bg-light opacity-50" />
        <div className="container-x relative grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
          <Reveal className="lg:col-span-5">
            <div className="inline-flex items-center gap-2 mb-5">
              <span className="w-8 h-px bg-emerald" />
              <span className="overline text-emerald">{c.intro_overline || "Who We Are"}</span>
            </div>
            <h2 className="font-display text-3xl sm:text-4xl md:text-5xl lg:text-6xl leading-[1.05] text-emerald">
              {c.intro_heading || "A holding company built for the next century, not the next quarter."}
            </h2>
          </Reveal>

          <Reveal delay={0.15} className="lg:col-span-6 lg:col-start-7 space-y-5 sm:space-y-6 text-foreground/75 text-base sm:text-lg leading-relaxed">
            {(c.intro_paragraph_1 || "").split("\n\n").filter(Boolean).map((p, i) => (
              <p key={`p1-${i}`}>{p}</p>
            ))}
            {(c.intro_paragraph_2 || "").split("\n\n").filter(Boolean).map((p, i) => (
              <p key={`p2-${i}`}>{p}</p>
            ))}
            <Link to={c.intro_link_href || "/p/the-asiabest-difference"} data-testid="home-about-link" className="inline-flex items-center gap-2 text-emerald font-medium link-underline hover:text-emerald-deep transition-colors">
              {c.intro_link_label || "Read our chairman's message"}
              <ArrowRight className="w-4 h-4" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* MARQUEE STRIP — bold typographic band */}
      {c.marquee_show !== false && (
        <MarqueeStrip items={c.marquee_items} />
      )}

      {/* MANIFESTO — big statement */}
      {c.manifesto_show !== false && (
        <Manifesto
          overline={c.manifesto_overline || "Our Belief"}
          words={c.manifesto_words || "We invest in *real* value."}
        />
      )}

      {/* FEATURED NEWS — Bento */}
      <section className="section-y bg-bone relative">
        <div className="container-x">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12 sm:mb-16">
            <Reveal>
              <div className="inline-flex items-center gap-2 mb-4">
                <span className="live-dot" style={{ background: "hsl(159 76% 10%)" }} />
                <span className="overline text-emerald">Newsfeed · Latest</span>
              </div>
              <h2 className="font-display text-3xl sm:text-4xl md:text-5xl lg:text-6xl text-emerald leading-[1.05]">Corporate news & updates</h2>
            </Reveal>
            <Reveal delay={0.15}>
              <Link to="/news" data-testid="home-news-link" className="btn-outline">
                All News <ArrowRight className="w-4 h-4" />
              </Link>
            </Reveal>
          </div>

          {featured.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-12 gap-3 sm:gap-4 md:gap-6">
              {/* Hero card */}
              <Reveal className="md:col-span-8 group">
                <Link to={`/news/${featured[0].slug}`} className="block relative overflow-hidden h-full border border-black/5 hover:border-emerald/30 transition-colors">
                  <div className="relative aspect-[16/10] overflow-hidden">
                    <img
                      src={featured[0].image_url || "https://images.unsplash.com/photo-1556761175-5973dc0f32e7?w=1600"}
                      alt={featured[0].title}
                      loading="lazy"
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-1000"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-emerald-ink via-emerald-ink/50 to-transparent" />
                    <div className="absolute top-4 left-4 inline-flex items-center gap-2 px-3 py-1 bg-emerald-ink/80 backdrop-blur-md border border-white/10">
                      <span className="live-dot" />
                      <span className="overline text-neon text-[0.6rem]">{featured[0].category}</span>
                    </div>
                    <div className="absolute bottom-0 left-0 right-0 p-6 sm:p-10 text-bone">
                      <h3 className="font-display text-2xl sm:text-3xl md:text-4xl lg:text-5xl leading-tight mb-3 sm:mb-4">{featured[0].title}</h3>
                      <p className="text-sm sm:text-base text-bone/80 max-w-2xl line-clamp-2">{featured[0].excerpt}</p>
                    </div>
                  </div>
                </Link>
              </Reveal>

              {/* Side cards */}
              <div className="md:col-span-4 grid grid-cols-1 gap-3 sm:gap-4 md:gap-6">
                {featured.slice(1, 4).map((n, i) => (
                  <Reveal key={n.id} delay={0.1 * (i + 1)}>
                    <Link to={`/news/${n.slug}`} className="block group p-5 sm:p-6 border border-black/5 hover:border-emerald/30 transition-all h-full bg-white relative">
                      <div className="flex items-center justify-between mb-3">
                        <span className="overline text-emerald text-[0.6rem]">{n.category}</span>
                        <span className="font-mono text-[0.65rem] text-foreground/40">{String(i + 2).padStart(2, "0")}</span>
                      </div>
                      <h4 className="font-display text-lg sm:text-xl leading-snug text-emerald group-hover:text-emerald-deep transition-colors line-clamp-3">{n.title}</h4>
                    </Link>
                  </Reveal>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* BENTO QUICKVIEW — multi-tile content overview */}
      {c.bento_show !== false && (
        <BentoQuickview
          latestNews={featured[0]}
          latestDisclosure={disclosures[0]}
          stock={stock}
          series={series}
          overline={c.bento_overline || "Quick View"}
          headingPrefix={c.bento_heading_prefix || "Everything that matters,"}
          headingEmphasis={c.bento_heading_emphasis || "in one glance"}
          headingSuffix={c.bento_heading_suffix || "."}
        />
      )}

      {/* DISCLOSURES PREVIEW */}
      <section className="section-y bg-muted/30 relative overflow-hidden">
        <div className="absolute inset-0 grid-bg-light opacity-50" />
        <div className="container-x relative grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
          <Reveal className="lg:col-span-5">
            <div className="inline-flex items-center gap-2 mb-5">
              <span className="w-8 h-px bg-emerald" />
              <span className="overline text-emerald">For Shareholders</span>
            </div>
            <h2 className="font-display text-3xl sm:text-4xl md:text-5xl mt-2 leading-[1.05] text-emerald">
              Public disclosures, <span className="accent-italic" style={{ color: "hsl(159 76% 10%)", textShadow: "none" }}>transparently</span> archived.
            </h2>
            <p className="mt-6 sm:mt-8 text-foreground/70 leading-relaxed text-sm sm:text-base">
              Every filing, every notice, every audited financial statement—available for the
              public investor community to inspect on demand.
            </p>
            <Link to="/disclosures" data-testid="home-disclosures-cta" className="btn-primary mt-8 sm:mt-10">
              <FileText className="w-4 h-4" />
              View All Disclosures
            </Link>
          </Reveal>

          <Reveal delay={0.1} className="lg:col-span-7">
            <div className="bg-white border border-black/5">
              {disclosures.length === 0 && (
                <div className="p-10 text-center text-foreground/50 text-sm">
                  No public disclosures uploaded yet. Check back soon.
                </div>
              )}
              {disclosures.map((d, i) => (
                <div key={d.id} className="flex items-center justify-between p-5 sm:p-6 border-b border-black/5 last:border-0 hover:bg-muted/40 transition-colors group">
                  <div className="flex items-start gap-4 min-w-0">
                    <span className="font-mono text-[0.7rem] text-foreground/30 mt-1.5">{String(i + 1).padStart(2, "0")}</span>
                    <FileText className="w-5 h-5 text-emerald flex-shrink-0 mt-0.5" />
                    <div className="min-w-0">
                      <div className="font-medium text-foreground group-hover:text-emerald transition-colors truncate">{d.title}</div>
                      <div className="text-xs text-foreground/50 mt-1 space-x-3 font-mono">
                        <span>{d.category}</span>
                        <span>·</span>
                        <span>{d.year}</span>
                      </div>
                    </div>
                  </div>
                  <a
                    href={`${api.defaults.baseURL}/disclosures/${d.id}/download`}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`home-disclosure-${d.id}`}
                    className="text-xs overline text-emerald link-underline transition-colors flex items-center gap-2 flex-shrink-0 ml-3"
                  >
                    PDF <ArrowRight className="w-3 h-3" />
                  </a>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* CAREERS BLOCK */}
      {c.careers_show !== false && (
        <CareersBlock
          overline={c.careers_overline || "Careers"}
          titlePrefix={c.careers_title_prefix || "Interested in"}
          titleEmphasis={c.careers_title_emphasis || "working"}
          titleSuffix={c.careers_title_suffix || "with us"}
          description={c.careers_description || ""}
          ctaLabel={c.careers_cta_label || "Apply Now"}
          ctaLink={c.careers_cta_link || "/contact"}
          imageUrl={c.careers_image_url || ""}
        />
      )}

      {/* CTA */}
      <section className="relative py-20 sm:py-24 lg:py-32 bg-emerald-ink text-bone overflow-hidden">
        <MeshGradient className="opacity-60" />
        <div className="absolute inset-0 grid-bg opacity-50" />
        <div className="grain" />
        <div className="container-x relative text-center">
          <Reveal>
            <div className="inline-flex items-center gap-2 mb-6">
              <span className="live-dot" />
              <span className="overline text-neon">{c.cta_overline || "Invest with Conviction"}</span>
            </div>
            <h2 className="font-display text-3xl sm:text-4xl md:text-5xl lg:text-7xl max-w-4xl mx-auto leading-[1.05]">
              <span className="gradient-text">{c.cta_heading || "Join a community of long-term shareholders building enduring Philippine institutions."}</span>
            </h2>
            <div className="mt-10 sm:mt-12 flex flex-col sm:flex-row flex-wrap items-stretch sm:items-center justify-center gap-3 sm:gap-4">
              <Link to={c.cta_button_link || "/contact"} data-testid="cta-contact" className="btn-primary">{c.cta_button_label || "Contact Investor Relations"}</Link>
              <Link to="/p/the-asiabest-difference" data-testid="cta-about" className="inline-flex items-center justify-center gap-2 text-bone/70 hover:text-neon link-underline transition-colors text-sm">
                Learn more about us <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
